// Content for the Disaster Support resources popup AND the permanent /resources
// page - ONE place, read by both, so a phone number only ever gets updated once.
//
// Source: events/cities/lynchburg/notes/disaster-bank.md in the vault, plus a
// verification pass on 2026-08-19 (utility, library, 211, county EM numbers).
// That file's own warning applies here too: stale emergency information is
// actively dangerous, so re-confirm anything here before leaning on it, and
// don't add a fact that wasn't actually seen on a real page.
//
// Design intent (Andrew via Lisa, disaster-bank.md): "lead with the Everbridge
// signup link" - the alert signup is the single highest-leverage thing someone
// can do before the next event, so it renders first and on its own, not buried
// in a list with everything else.

// The lyh_filter_groups slug for the Disaster Support chip. Andrew turns it on
// with one SQL update (see the lyh-calendar SKILL.md "Disaster Support chip"
// section) - this constant is what App.tsx and FilterDrawer.tsx use to
// recognize that row and treat it specially: pinned first regardless of sort,
// rendered with a green cross instead of a plain pill, shown even at zero
// events, and wired to also open the resources popup when selected.
export const DISASTER_SUPPORT_SLUG = 'disaster-support'

export interface ResourceLink {
  label: string
  href: string
  detail?: string
}

export interface ResourceSection {
  title: string
  items: ResourceLink[]
}

export const EVERBRIDGE_SIGNUP: ResourceLink = {
  label: 'Sign up for Lynchburg Alerts',
  href: 'https://member.everbridge.net/453003085611478/login',
  detail:
    "The city's official emergency system (Everbridge) - severe weather, road closures, evacuations, missing persons - by phone, text and email. Registration is required: \"If we can't reach you, we can't alert you.\"",
}

export const DISASTER_RESOURCE_SECTIONS: ResourceSection[] = [
  {
    title: 'Utilities',
    items: [
      {
        label: 'Appalachian Power outage line',
        href: 'https://www.appalachianpower.com/outages/',
        detail: 'Report or check an outage: 1-800-956-4237. Live outage map at outagemap.appalachianpower.com.',
      },
      {
        label: 'Lynchburg Water Resources',
        href: 'https://www.lynchburgva.gov/1014/Water',
        detail: '24/7 emergency line: 434-455-4250. Billing questions M-F 8:30-5: 434-455-3840.',
      },
    ],
  },
  {
    title: 'Free wifi',
    items: [
      {
        label: 'Lynchburg Public Library - Downtown Branch',
        href: 'https://lynchburgpubliclibrary.org/locations-hours/',
        detail: '216 12th St, Lynchburg. M/W/F 10-6, Tu/Th 10-8, Sat 9-5. Free public wifi.',
      },
      {
        label: 'Lynchburg Public Library - Miller Center Hub',
        href: 'https://lynchburgpubliclibrary.org/locations-hours/',
        detail: '301 Grove St, Lynchburg. M-F 10-6, Sat 11-4. Free public wifi. (The Memorial Ave main branch is closed for renovation.)',
      },
      {
        label: 'Starbucks - Wards Road',
        href: 'https://www.starbucks.com/store-locator/store/8286/us/wards-road/3911-wards-road-suite-a-lynchburg-va-245023245',
        detail: '3911 Wards Road, Suite A, Lynchburg. Free wifi with any purchase. Check the Starbucks store locator for other Lynchburg locations.',
      },
    ],
  },
  {
    title: 'Help beyond 911',
    items: [
      {
        label: '211 Virginia',
        href: 'https://211virginia.org/contact-us/',
        detail: 'Dial 211, or 1-800-230-6977 (TTY: 711). Free, confidential, 24/7 - food, shelter, utility help and more.',
      },
      {
        label: 'American Red Cross',
        href: 'https://www.redcross.org/get-help/disaster-relief-and-recovery-services/find-an-open-shelter.html',
        detail: '1-800-733-2767, 24/7. Their shelter-finder map shows open shelters during an active disaster.',
      },
    ],
  },
  {
    title: 'Shelters, warming and cooling centers',
    items: [
      {
        label: 'Salvation Army Center of Hope',
        href: 'https://southernusa.salvationarmy.org/lynchburg/center-of-hope/',
        detail: '2215 Park Ave, Lynchburg, 434-845-5939. Opens as a warming station below 40°F and a cooling station above 90°F; serves Lynchburg, Amherst, Appomattox, Bedford, Campbell and Nelson.',
      },
      {
        label: 'Bedford County Emergency Management',
        href: 'https://www.bedfordcountyva.gov/government/administration/emergency-management',
        detail: '540-587-0700, 1185 Turning Point Rd, Bedford.',
      },
      {
        label: 'Campbell County Public Safety (non-911)',
        href: 'https://www.co.campbell.va.us/216/Public-Safety',
        detail: '434-332-9574, 34 Communications Ln, Rustburg.',
      },
      {
        label: 'Lynchburg CivicAlerts',
        href: 'https://www.lynchburgva.gov/CivicAlerts.aspx',
        detail: "Where the city posts official updates during an incident (e.g. water advisories). City contact: 900 Church St, 434-856-2489, M-F 8:30-5.",
      },
    ],
  },
]

/** Amherst County: no dedicated non-emergency EM line or warming/cooling page was
 * found as of 2026-08-19 - Fire & EMS coordinates emergency response there. Left
 * out of the list above rather than guessed; add it once a real number turns up. */
